<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :title="title"
      :beforeSetupGrid="beforeSetupGrid"
      :actions="actions"
      ref="grid"
      :selectionEnabled="false"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
    ></dx-grid>

    <v-dialog persistent v-model="createNew.dialog" max-width="600">
      <v-card class="info-card">
        <div class="info-dialog-header info-dialog-header--primary">
          <v-icon dark class="mr-2">mdi-plus-circle-outline</v-icon>
          <span class="info-toolbar-title">New Information</span>
        </div>

        <v-card-text class="pa-4">
          <v-form ref="formNew">
            <v-text-field
              v-model="createNew.description"
              label="Description"
              placeholder="Enter a Description"
              prepend-inner-icon="mdi-text-short"
              outlined
              :rules="[rules.required]"
            />

            <v-text-field
              v-model="createNew.link"
              label="Link"
              placeholder="Enter the link"
              prepend-inner-icon="mdi-link-variant"
              outlined
              :rules="[rules.pathOrUrl, rules.required]"
            />
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn small color="grey darken-1" dark @click="resetNew()">
            <v-icon left small>mdi-close</v-icon>Cancel
          </v-btn>
          <v-btn small :loading="processing" color="success" @click="saveNew()">
            <v-icon left small>mdi-content-save</v-icon>Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showDetailsDialog" max-width="600">
      <v-card class="info-card" v-if="selectedRow">
        <div class="info-dialog-header info-dialog-header--primary">
          <v-icon dark class="mr-2">mdi-information-outline</v-icon>
          <span class="info-toolbar-title">Information Details</span>
          <v-chip small color="white" text-color="primary" class="ml-2 font-weight-bold">
            #{{ selectedRow.id }}
          </v-chip>
        </div>

        <v-card-text class="px-4 pt-0">
          <div class="info-meta mb-3">
            <v-chip title="Create By" small label class="info-meta-chip mr-2 mb-1">
              <v-icon left small>mdi-account</v-icon>
              {{ selectedRow.createBy }}
            </v-chip>
            <v-chip title="Create Date" small label class="info-meta-chip mb-1">
              <v-icon left small>mdi-calendar-plus</v-icon>
              {{ formatDate(selectedRow.createAt) }}
            </v-chip>
            <template
              v-if="selectedRow.lastUpdateBy || selectedRow.lastUpdatedAt"
            >
              <div style="width: 100%;"></div>
              <v-chip
                title="Last Updated By"
                small
                label
                class="info-meta-chip info-meta-chip--update mr-2 mb-1"
              >
                <v-icon left small>mdi-account-edit</v-icon>
                {{ selectedRow.lastUpdateBy }}
              </v-chip>
              <v-chip
                title="Last Updated Date"
                small
                label
                class="info-meta-chip info-meta-chip--update mb-1"
              >
                <v-icon left small>mdi-calendar-edit</v-icon>
                {{ formatDate(selectedRow.lastUpdatedAt) }}
              </v-chip>
            </template>
          </div>

          <div v-if="!editMode">
            <div class="info-field">
              <div class="info-field-label">
                <v-icon small color="grey darken-1" class="mr-1"
                  >mdi-text-short</v-icon
                >
                Description
              </div>
              <div class="info-field-value">
                {{ selectedRow.description }}
              </div>
            </div>

            <div class="info-field">
              <div class="info-field-label">
                <v-icon small color="grey darken-1" class="mr-1"
                  >mdi-link-variant</v-icon
                >
                Link
              </div>
              <div class="info-field-value">
                <a
                  :href="selectedRow.link"
                  class="info-link"
                  @click.prevent="() => handleLinkClick(selectedRow.link)"
                >
                  {{ selectedRow.link }}
                  <v-icon small color="primary" class="ml-1"
                    >mdi-open-in-new</v-icon
                  >
                </a>
              </div>
            </div>
          </div>
          <div v-else>
            <v-form ref="formEdit">
              <v-text-field
                v-model="edit.description"
                label="Description"
                placeholder="Enter a Description"
                prepend-inner-icon="mdi-text-short"
                outlined
                :rules="[rules.required]"
                class="mb-2"
              />

              <v-text-field
                v-model="edit.link"
                label="Link"
                placeholder="Enter the link"
                prepend-inner-icon="mdi-link-variant"
                outlined
                :rules="[rules.pathOrUrl, rules.required]"
              />
            </v-form>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <template v-if="!editMode">
            <v-btn small color="grey darken-1" dark @click="closeDetailsDialog">
              <v-icon left small>mdi-close</v-icon>Close
            </v-btn>
            <v-btn small color="error" @click="deleteDialog = true">
              <v-icon left small>mdi-delete</v-icon>Delete
            </v-btn>
            <v-btn small color="success" @click="enterEditMode">
              <v-icon left small>mdi-pencil</v-icon>Edit
            </v-btn>
          </template>
          <template v-else>
            <v-btn small color="grey darken-1" dark @click="cancelEdit">
              <v-icon left small>mdi-close</v-icon>Cancel
            </v-btn>
            <v-btn small :loading="processing" color="success" @click="saveChanges">
              <v-icon left small>mdi-content-save</v-icon>Save Changes
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card class="info-card">
        <div class="info-dialog-header info-dialog-header--error">
          <v-icon dark class="mr-2">mdi-alert-circle-outline</v-icon>
          <span class="info-toolbar-title">Confirm Delete</span>
        </div>
        <v-card-text class="pt-4 pb-2">
          Are you sure you want to delete this item? This action cannot be
          undone.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn small color="grey darken-1" dark @click="deleteDialog = false">
            <v-icon left small>mdi-close</v-icon>Cancel
          </v-btn>
          <v-btn small color="error" :loading="processing" @click="softDelete">
            <v-icon left small>mdi-delete</v-icon>Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  metaInfo() {
    return {
      title: this.title,
    };
  },

  data: () => ({
    url: "",
    title: "Help Desk Information",
    actions: [],
    processing: false,
    copyURL: false,
    targetURL: true,
    selectedRow: null,
    showDetailsDialog: false,
    editMode: false,
    deleteDialog: false,
    createNew: {
      dialog: false,
      description: null,
      link: null,
    },

    edit: {
      link: null,
      description: null,
    },

    rules: {
      required: (value) => !!value || "Required.",
      pathOrUrl: (v) => {
        if (!v) return true;
        const isValidURL = URL.canParse(v);
        if (isValidURL) return isValidURL;
        return "Invalid link";
      },
    },
    columns: [
      {
        label: "Id",
        field: "id",
        visible: true,
        sortOrder: "asc",
        sortIndex: 0,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        cssClass: "width-50",
      },
      {
        label: "Description",
        field: "description",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        cssClass: "width-400",
      },
      {
        label: "Link",
        field: "link",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        template: "href-action-cell-template",
        cssClass: "width-600",
      },
      {
        label: "Create By",
        field: "createBy",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        cssClass: "width-150",
      },
      {
        label: "Create Date",
        field: "createAt",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy hh:mm a",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        cssClass: "width-150",
      },
    ],
  }),
  created() {
    const nameColumn = this.columns.find((c) => c.field === "link");
    if (nameColumn) {
      nameColumn.linkAction = (row) => {
        this.handleLinkClick(row.link);
      };
    }

    this.actions.push({
      text: "New",
      action: this.OpenCreateNewDialog,
    });
  },
  methods: {
    async beforeSetupGrid() {
      localStorage.getItem("help-desk-info-copy-url") == "true"
        ? (this.copyURL = true)
        : "";

      localStorage.getItem("help-desk-info-target-url") == "false"
        ? (this.targetURL = false)
        : "";

      this.url = `HelpDeskInformation`;

      this.actions.push({
        text: "URL ⚙️",
        type: "menu-switches",
        items: [
          {
            text: "Copy URL",
            value: this.copyURL,
            action: this.toggleCopyURL,
          },
          {
            text: "Open in new tab",
            value: this.targetURL,
            action: this.toggleTargetURL,
          },
        ],
      });
    },

    OpenCreateNewDialog() {
      this.createNew.dialog = true;
    },
    resetNew() {
      this.createNew = {
        dialog: false,
        description: null,
        link: null,
      };
      this.$refs.formNew.resetValidation(true);
    },
    async saveNew() {
      if (!this.$refs.formNew.validate(true)) {
        return;
      }

      this.processing = true;
      try {
        await this.$http.post("api/HelpDeskInformation/Create", {
          Description: this.createNew.description,
          Link: this.createNew.link,
        });
        this.$refs.grid.reloadGrid();
        this.processing = false;
        this.$notify({
          group: "notification",
          text: "New info created",
          type: "success",
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.resetNew();
      }
    },
    handleLinkClick(link) {
      try {
        const url = new URL(link);
        if (
          !this.copyURL &&
          (url.protocol == "http:" || url.protocol == "https:")
        ) {
          open(link, this.targetURL ? "_blank" : "_self");
          return;
        } else {
          this.copy(link);
        }
      } catch (error) {
        this.copy(link);
        console.log(error);
      }
    },
    async copy(link) {
      await navigator.clipboard.writeText(link);
      this.$notify({
        group: "notification",
        text: `Link copied to the clipboard`,
        type: "success",
      });
    },
    toggleCopyURL(v) {
      localStorage.setItem("help-desk-info-copy-url", v);
      this.copyURL = v;
    },
    toggleTargetURL(v) {
      localStorage.setItem("help-desk-info-target-url", v);
      this.targetURL = v;
    },
    onRowClicked(v) {
      this.selectedRow = v;
      this.showDetailsDialog = true;
    },
    closeDetailsDialog() {
      this.editMode = false;
      this.deleteDialog = false;
      this.resetEdit();
      if (this.$refs.formEdit) {
        this.$refs.formEdit.resetValidation(true);
      }
      this.showDetailsDialog = false;
      this.selectedRow = null;
    },
    formatDate(date) {
      if (!date) return "N/A";
      const d = new Date(date);
      return d.toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    initializeEdit() {
      if (!this.selectedRow) return;
      this.edit.link = this.selectedRow.link;
      this.edit.description = this.selectedRow.description;
    },
    enterEditMode() {
      this.initializeEdit();
      this.editMode = true;
    },
    resetEdit() {
      this.edit = {
        link: null,
        description: null,
      };
    },
    cancelEdit() {
      this.editMode = false;
      this.resetEdit();
      if (this.$refs.formEdit) {
        this.$refs.formEdit.resetValidation(true);
      }
    },
    async saveChanges() {
      if (!this.$refs.formEdit.validate(true)) {
        return;
      }
      if (
        this.edit.link == this.selectedRow.link &&
        this.edit.description == this.selectedRow.description
      ) {
        return this.$notify({
          group: "notification",
          text: "No changes detected",
          type: "info",
        });
      }
      this.processing = true;
      try {
        await this.$http.put(
          `api/HelpDeskInformation/Update/${this.selectedRow.id}`,
          {
            Description: this.edit.description,
            Link: this.edit.link,
          }
        );
        this.$refs.grid.reloadGrid();
        this.$notify({
          group: "notification",
          text: "Info updated successfully",
          type: "success",
        });
        this.editMode = false;
        this.closeDetailsDialog();
      } catch (e) {
        console.error(e);
      } finally {
        this.processing = false;
        this.resetEdit();
      }
    },
    async softDelete() {
      try {
        this.processing = true;
        await this.$http.put(
          `api/HelpDeskInformation/SoftDelete/${this.selectedRow.id}`
        );
        this.$refs.grid.reloadGrid();
        this.$notify({
          group: "notification",
          text: "Info deleted",
          type: "success",
        });
        this.editMode = false;
        this.closeDetailsDialog();
        this.resetEdit();
      } catch (e) {
        console.error(e);
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<style scoped>
.info-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.info-dialog-header {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-radius: 12px 12px 0 0;
  min-height: 48px;
}

.info-dialog-header--primary {
  background-color: #4caf50;
}

.info-dialog-header--error {
  background-color: #f44336;
}

.info-toolbar-title {
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.2px;
  color: #fff;
}

.info-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.info-meta-chip {
  background-color: #eef2f7 !important;
  color: #455a64 !important;
  font-weight: 500;
}

.info-meta-chip--update {
  background-color: #fff3e0 !important;
  color: #ef6c00 !important;
}

.info-field {
  background-color: #f8fafc;
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.info-field:hover {
  border-color: #c5d2e0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.info-field-label {
  display: flex;
  align-items: center;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #607d8b;
  margin-bottom: 4px;
  font-weight: 600;
}

.info-field-value {
  font-size: 15px;
  font-weight: 500;
  color: #263238;
  line-height: 1.5;
}

.info-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  word-break: break-all;
}

.info-link:hover {
  text-decoration: underline;
}
</style>
